import React, { useState, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Avatar,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { FaPlus, FaUserMd, FaEnvelope, FaPhone, FaEdit, FaTrash, FaClock, FaBan, FaCalendarAlt } from "react-icons/fa";
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getConsultants, searchConsultants, deleteConsultant, createConsultant, updateConsultant, getConsultantById } from '../services/consultantService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import CustomModal from '../components/Modal';
import ConsultantForm from '../components/forms/ConsultantForm';
import ConsultantWeeklySchedule from '../components/ConsultantWeeklySchedule';
import Pagination from '../components/Pagination';

function Consultants() {
  const formRef = useRef(null);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const textColor = useColorModeValue("gray.700", "white");
  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  
  // State برای جستجو و modal
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConsultant, setEditingConsultant] = useState(null);
  const [isWeeklyModalOpen, setIsWeeklyModalOpen] = useState(false);
  const [weeklyConsultant, setWeeklyConsultant] = useState(null);
  
  // React Query hooks
  const queryClient = useQueryClient();
  
  // دریافت لیست مشاوران
  const {
    data: consultantsData,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['consultants', currentPage, itemsPerPage, searchTerm],
    () => getConsultants({
      page: currentPage,
      limit: itemsPerPage,
      search: searchTerm || undefined,
    }),
    {
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000, // 2 دقیقه - کاهش زمان cache برای آمار به‌روز
    }
  );

  // Mutation برای حذف مشاور
  const deleteConsultantMutation = useMutation(deleteConsultant, {
    onSuccess: () => {
      toast.success('مشاور با موفقیت حذف شد');
      queryClient.invalidateQueries(['consultants']);
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در حذف مشاور');
    }
  });

  // Mutation برای ایجاد مشاور
  const createConsultantMutation = useMutation(createConsultant, {
    onSuccess: () => {
      toast.success('مشاور با موفقیت اضافه شد');
      queryClient.invalidateQueries(['consultants']);
      setIsModalOpen(false);
      setEditingConsultant(null);
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در افزودن مشاور');
    }
  });

  // Mutation برای تولید کد برای مشاوران
  const generateCodesMutation = useMutation(async () => {
    const response = await fetch('/api/users/generate-codes', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    return response.json();
  }, {
    onSuccess: (data) => {
      toast.success(data.message || 'کدها با موفقیت تولید شدند');
      queryClient.invalidateQueries(['consultants']);
    },
    onError: (error) => {
      console.error('🔍 generateCodesMutation - خطا:', error);
      toast.error(error.message || 'خطا در تولید کدها');
    }
  });

  // Mutation برای بروزرسانی مشاور
  const updateConsultantMutation = useMutation(updateConsultant, {
    onSuccess: (data) => {
      
      // نمایش پیام موفقیت از backend
      if (data?.message) {
        toast.success(data.message);
      } else {
        toast.success('مشاور با موفقیت بروزرسانی شد');
      }
      
      queryClient.invalidateQueries(['consultants']);
      setIsModalOpen(false);
      setEditingConsultant(null);
    },
    onError: (error) => {
      console.error('🔍 updateConsultantMutation - خطا:', error);
      toast.error(error.message || 'خطا در بروزرسانی مشاور');
    }
  });

  // تابع حذف مشاور
  const handleDeleteConsultant = (consultantId) => {
    if (window.confirm('آیا از حذف این مشاور اطمینان دارید؟')) {
      deleteConsultantMutation.mutate(consultantId);
    }
  };

  // تابع باز کردن modal برای افزودن
  const handleAddConsultant = () => {
    setEditingConsultant(null);
    setIsModalOpen(true);
  };

  // تابع تولید کد برای مشاوران
 

  // تابع باز کردن modal برای ویرایش
  const handleEditConsultant = async (consultant) => {
    try {
      // دریافت اطلاعات کامل مشاور از سرور
      const fullConsultantData = await getConsultantById(consultant._id || consultant.id);
      const fullConsultant = fullConsultantData.data.user;
        
      // تبدیل نام کامل به نام و نام خانوادگی
      const nameParts = fullConsultant.name ? fullConsultant.name.split(' ') : ['', ''];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      // پاک کردن داده‌های مشاور برای جلوگیری از circular reference
      const cleanConsultant = {
        _id: fullConsultant._id,
        id: fullConsultant.id,
        name: fullConsultant.name,
        firstName: firstName,
        lastName: lastName,
        email: fullConsultant.email,
        phone: fullConsultant.phone,
        code: fullConsultant.code || '', // اضافه کردن کد مشاور
        specialty: fullConsultant.specialty || 'general',
        licenseNumber: fullConsultant.licenseNumber || '',
        experience: fullConsultant.experience || '',
        education: fullConsultant.education || '',
        bio: fullConsultant.bio || '',
        hourlyRate: fullConsultant.hourlyRate || 0,
        deposit:fullConsultant?.deposit || 0,
        sessionDuration: fullConsultant.sessionDuration || 60,
        active: fullConsultant.active !== false,
        showDuration: fullConsultant?.showDuration || 45,
        appointmentEndDate: fullConsultant.appointmentEndDate
          ? (typeof fullConsultant.appointmentEndDate === 'string' && fullConsultant.appointmentEndDate.includes('T')
              ? fullConsultant.appointmentEndDate.slice(0, 10)
              : fullConsultant.appointmentEndDate)
          : '',
        paymentLink: fullConsultant.paymentLink || ''
      };
      
      // بررسی اینکه آیا مشاور کد دارد
      if (!fullConsultant.code) {
        toast.info('کد مشاور خودکار تولید می‌شود');
      }
      
      setEditingConsultant(cleanConsultant);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching consultant details:', error);
      toast.error('خطا در دریافت اطلاعات مشاور');
    }
  };

  // تابع بستن modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingConsultant(null);
  };

  // تابع ارسال فرم
  const handleSubmitForm = async (data) => {
    // اعتبارسنجی فرم قبل از ارسال
    if (formRef.current) {
      const validationResult = await formRef.current.trigger();
      
      if (!validationResult.isValid) {
        toast.error('لطفاً خطاهای فرم را برطرف کنید');
        return;
      }
    }
    
    // پاک کردن داده‌های فرم برای جلوگیری از circular reference
    const cleanData = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      role: 'consultant', // اضافه کردن نقش مشاور
      specialty: data.specialty,
      licenseNumber: data.licenseNumber,
      experience: data.experience || '',
      education: data.education || '',
      bio: data.bio || '',
      hourlyRate: data.hourlyRate ? parseFloat(data.hourlyRate) : 0,
      deposit:data.deposit ? parseFloat(data.deposit) : 0,
      sessionDuration: data.sessionDuration ? parseInt(data.sessionDuration) : 60,
      showDuration: data.showDuration ? parseInt(data.showDuration) : 60,
      active: data.active !== false,
      appointmentEndDate: data.appointmentEndDate && data.appointmentEndDate.trim() !== '' ? data.appointmentEndDate : '',
      paymentLink: data.paymentLink && String(data.paymentLink).trim() !== '' ? String(data.paymentLink).trim() : ''
    };

    if (editingConsultant) {
      // ویرایش مشاور موجود
    
      
      updateConsultantMutation.mutate({ 
        consultantId: editingConsultant._id || editingConsultant.id, 
        consultantData: cleanData 
      });
    } else {
      // افزودن مشاور جدید
      createConsultantMutation.mutate(cleanData);
    }
  };

  // تابع جستجو
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // تابع رفتن به صفحه ساعات کاری
  const handleWorkingHours = (consultantId) => {
    console.log('Navigating to working hours for consultant:', consultantId);
    navigate(`/working-hours?consultant=${consultantId}`);
  };

  const handleWeeklySchedule = (consultant) => {
    setWeeklyConsultant(consultant);
    setIsWeeklyModalOpen(true);
  };

  // تابع تبدیل تخصص به رنگ
  const getSpecialtyColor = (specialty) => {
    return 'teal'; // رنگ ثابت برای همه تخصص‌ها
  };

  // تابع تبدیل تخصص به فارسی
  const getSpecialtyText = (specialty) => {
    return specialty || 'تخصص ثبت نشده';
  };

  // اگر در حال بارگذاری است
  if (isLoading) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Flex justify="center" align="center" minH="400px">
              <Spinner size="xl" color="teal.500" />
              <Text mr={4} fontSize="lg" color={textColor}>
                در حال بارگذاری مشاوران...
              </Text>
            </Flex>
          </Box>
        </Box>
      </Flex>
    );
  }

  // اگر خطا رخ داده است
  if (error) {
    return (
      <Flex direction="column" pt={{ base: "120px", md: "75px" }} dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <AlertTitle>خطا در بارگذاری داده‌ها!</AlertTitle>
              <AlertDescription>
                {error.message || 'خطا در ارتباط با سرور'}
              </AlertDescription>
            </Alert>
            <Button 
              mt={4} 
              colorScheme="teal" 
              onClick={() => refetch()}
              isLoading={isLoading}
            >
              تلاش مجدد
            </Button>
          </Box>
        </Box>
      </Flex>
    );
  }

  // داده‌های مشاوران - بهبود دسترسی به داده‌ها
  const consultants = consultantsData?.data?.consultants || 
                     consultantsData?.consultants || 
                     consultantsData?.data || 
                     consultantsData || [];
  const consultantsPagination = consultantsData?.data?.pagination || consultantsData?.pagination;

  return (
    <>
      <Flex direction="column"  dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Box bg={bgCard} borderRadius="15px" boxShadow="0 20px 27px 0 rgb(0 0 0 / 5%)" p="20px">
            <Box p="20px 0px 35px 0px">
              <Flex direction="column">
                <Text fontSize="lg" color={textColor} fontWeight="bold" me="auto">
                  مدیریت مشاوران
                </Text>
                <Text fontSize="md" fontWeight="400" me="auto" color="gray.400">
                  مشاهده و مدیریت اطلاعات مشاوران کلینیک
                </Text>
              </Flex>
            </Box>
            <Box px="0px" pt="0px" pb="25px">
              <Flex px="25px" mb="25px" justifyContent="space-between" alignItems="center">
                <HStack spacing="15px">
                  <InputGroup maxW="400px">
                    <InputLeftElement>
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="جستجو در مشاوران..."
                      borderRadius="15px"
                      fontSize="sm"
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                  </InputGroup>
                </HStack>
                <Button
                  leftIcon={<FaPlus />}
                  colorScheme="teal"
                  borderRadius="15px"
                  fontSize="sm"
                  fontWeight="bold"
                  px="20px"
                  py="10px"
                  onClick={handleAddConsultant}
                >
                  افزودن مشاور جدید
                </Button>
              </Flex>
              
              {/* جدول مشاوران */}
              <Box overflowX={{ sm: "scroll", xl: "hidden" }}>
                <Table variant="simple" color={textColor} size="sm">
                  <Thead>
                    <Tr my=".8rem" pl="0px" color="gray.400">
                      <Th borderColor={borderColor} textAlign="right">
                        مشاور
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        کد
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        تخصص
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        اطلاعات تماس
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        وضعیت
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        پایان نوبت‌دهی
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        تعداد مراجعان
                      </Th>
                      <Th borderColor={borderColor} textAlign="right">
                        عملیات
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {consultants.length === 0 ? (
                      <Tr>
                        <Td colSpan={8} textAlign="center" py={8}>
                          <Text color="gray.500">هیچ مشاوری یافت نشد</Text>
                        </Td>
                      </Tr>
                    ) : (
                      consultants.map((consultant) => (
                        <Tr key={consultant._id || consultant.id}>
                          <Td minWidth={{ sm: "250px" }} pl="0px">
                            <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
                              <Avatar
                                size="md"
                                name={consultant.firstName && consultant.lastName 
                                  ? `${consultant.firstName} ${consultant.lastName}`
                                  : consultant.name || 'نامشخص'
                                }
                                bg="teal.500"
                                me="18px"
                              />
                              <Flex direction="column">
                                <Text
                                  fontSize="md"
                                  color={textColor}
                                  fontWeight="bold"
                                  minWidth="100%"
                                >
                                  {consultant.firstName && consultant.lastName 
                                    ? `${consultant.firstName} ${consultant.lastName}`
                                    : consultant.name || 'نامشخص'
                                  }
                                </Text>
                                <Text fontSize="sm" color="gray.400" fontWeight="normal">
                                  {consultant.licenseNumber || 'شماره پروانه ثبت نشده'}
                                </Text>
                              </Flex>
                            </Flex>
                          </Td>
                          <Td>
                            <Text fontSize="md" color={textColor} fontWeight="bold" pb=".5rem">
                              {consultant.code || 'کد ثبت نشده'}
                            </Text>
                          </Td>
                          <Td>
                            <Badge
                              bg={`${getSpecialtyColor(consultant.specialty)}.400`}
                              color="white"
                              fontSize="16px"
                              p="3px 10px"
                              borderRadius="8px"
                            >
                              {getSpecialtyText(consultant.specialty)}
                            </Badge>
                          </Td>
                          <Td>
                            <Flex direction="column" alignItems="flex-start">
                              <HStack spacing={2} mb={1}>
                                <FaEnvelope color="gray.400" />
                                <Text fontSize="sm" color={textColor}>
                                  {consultant.email || 'ایمیل ثبت نشده'}
                                </Text>
                              </HStack>
                              <HStack spacing={2}>
                                <FaPhone color="gray.400" />
                                <Text fontSize="sm" color={textColor}>
                                  {consultant.phone || 'شماره تماس ثبت نشده'}
                                </Text>
                              </HStack>
                            </Flex>
                          </Td>
                          <Td>
                            <Badge
                              bg={consultant.active !== false ? "green.400" : "gray.400"}
                              color="white"
                              fontSize="16px"
                              p="3px 10px"
                              borderRadius="8px"
                            >
                              {consultant.active !== false ? "فعال" : "غیرفعال"}
                            </Badge>
                          </Td>
                          <Td>
                            <Text fontSize="sm" color={textColor}>
                              {consultant.appointmentEndDate
                                ? new Date(consultant.appointmentEndDate).toLocaleDateString('fa-IR', { year: 'numeric', month: '2-digit', day: '2-digit' })
                                : 'آزاد'}
                            </Text>
                          </Td>
                          <Td>
                            <Text fontSize="md" color={textColor} fontWeight="bold" pb=".5rem">
                              {consultant.clientCount || 0} نفر
                            </Text>
                          </Td>
                          <Td>
                            <HStack spacing="2">
                              <Button
                                size="sm"
                                colorScheme="teal"
                                variant="outline"
                                leftIcon={<FaEdit />}
                                onClick={() => handleEditConsultant(consultant)}
                              >
                                ویرایش
                              </Button>
                              <Button
                                size="sm"
                                colorScheme="blue"
                                variant="outline"
                                leftIcon={<FaClock />}
                                onClick={() => handleWorkingHours(consultant._id || consultant.id)}
                              >
                                ساعات کاری
                              </Button>
                              <Button
                                size="sm"
                                colorScheme="purple"
                                variant="outline"
                                leftIcon={<FaCalendarAlt />}
                                onClick={() => handleWeeklySchedule(consultant)}
                              >
                                جدول هفتگی
                              </Button>
                              {(isAdmin && isAdmin()) && (
                                <Button
                                  size="sm"
                                  colorScheme="orange"
                                  variant="outline"
                                  leftIcon={<FaBan />}
                                  onClick={() => navigate(`/unavailable-hours?consultant=${consultant._id || consultant.id}`)}
                                >
                                  ساعات غیرفعال
                                </Button>
                              )}
                              <Button
                                size="sm"
                                colorScheme="red"
                                variant="outline"
                                leftIcon={<FaTrash />}
                                onClick={() => handleDeleteConsultant(consultant._id || consultant.id)}
                                isLoading={deleteConsultantMutation.isLoading}
                              >
                                حذف
                              </Button>
                            </HStack>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>

              {/* Pagination */}
              {consultantsPagination && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={consultantsPagination.totalPages}
                  totalItems={consultantsPagination.totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Flex>

      {/* Modal فرم مشاور */}
      <CustomModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingConsultant ? 'ویرایش مشاور' : 'افزودن مشاور جدید'}
        onSubmit={async () => {
          if (formRef.current) {
            const validationResult = await formRef.current.validateAndSubmit();
            if (!validationResult.isValid) {
              toast.error('لطفاً خطاهای فرم را برطرف کنید');
            }
          }
        }}
        isLoading={createConsultantMutation.isLoading || updateConsultantMutation.isLoading}
        submitText={editingConsultant ? 'بروزرسانی' : 'افزودن'}
        size="xl"
      >
        <ConsultantForm
          ref={formRef}
          consultant={editingConsultant}
          onSubmit={handleSubmitForm}
          isLoading={createConsultantMutation.isLoading || updateConsultantMutation.isLoading}
        />
      </CustomModal>

      {/* Modal جدول هفتگی مشاور */}
      <CustomModal
        isOpen={isWeeklyModalOpen}
        onClose={() => {
          setIsWeeklyModalOpen(false);
          setWeeklyConsultant(null);
        }}
        title={
          weeklyConsultant
            ? `جدول هفتگی – ${weeklyConsultant.firstName && weeklyConsultant.lastName
                ? `${weeklyConsultant.firstName} ${weeklyConsultant.lastName}`
                : weeklyConsultant.name || 'مشاور'}`
            : 'جدول هفتگی'
        }
        onSubmit={null}
        hideFooter
        size="6xl"
      >
        <ConsultantWeeklySchedule consultant={weeklyConsultant} />
      </CustomModal>
    </>
  );
}

export default Consultants; 